// The API, typed by the contract (shared/src/api.ts). Each call carries its pool's token; an answer the API refuses
// comes back as an ApiError with the contract's code.
import type { paths } from '@tune/shared';
import createClient, { type Client } from 'openapi-fetch';

export type Api = Client<paths>;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    /** The contract's code: `not_found`, `changed`, `quota_exceeded`… */
    readonly code: string,
    message: string,
    /** The refusal as the API sent it, with what else it says (a 412's newer values, say). */
    readonly body: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

/**
 * A client whose calls carry the pool's access token. When the API refuses one (401), the session is renewed and
 * the call tried once more; refused again, the pool is signed out.
 */
export function makeApi(token: (renew?: boolean) => Promise<string | null>, signedOut: () => void): Api {
  const send = async (request: Request, renew: boolean): Promise<Response> => {
    const t = await token(renew);
    const attempt = request.clone();
    if (t) {
      attempt.headers.set('Authorization', `Bearer ${t}`);
    }
    return fetch(attempt);
  };
  return createClient<paths>({
    baseUrl: '/v1',
    fetch: async (request: Request) => {
      const first = await send(request, false);
      if (first.status !== 401) {
        return first;
      }
      const again = await send(request, true);
      if (again.status === 401) {
        signedOut();
      }
      return again;
    },
  });
}

/** What a call answered, or its refusal thrown as an ApiError. */
export async function data<T>(call: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  let answer;
  try {
    answer = await call;
  } catch {
    throw new ApiError(0, 'offline', "TUNE's cloud didn't answer");
  }
  const { data, error, response } = answer;
  if (error !== undefined || !response.ok) {
    const e = (error ?? {}) as Record<string, unknown>;
    throw new ApiError(
      response.status,
      typeof e.error === 'string' ? e.error : 'unknown',
      typeof e.message === 'string' ? e.message : `the API answered ${response.status}`,
      e,
    );
  }
  return data as T;
}

/** What to tell someone when a call failed for a reason the page doesn't handle itself. */
export function problem(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === 'offline') {
      return "TUNE's cloud didn't answer. Check the connection and try again.";
    }
    if (error.status === 429) {
      return 'That was a lot of tries. Wait a minute, then try again.';
    }
    if (error.status >= 500) {
      return 'Something went wrong on our side. Try again in a minute.';
    }
  }
  return 'Something went wrong. Try again.';
}
